import React, { useEffect, useState } from "react";
import { Dropdown } from "@fluentui/react-northstar";
import find from "lodash/find";
import filter from "lodash/filter";
import IUser from "../../interfaces/IUser";
import { useUsers } from "../../queries";

export interface IUserPickerProps {
    selectedUserId?: string;
    userSelected: (user?: IUser) => void;
}

const UserPicker: React.FunctionComponent<IUserPickerProps> = ({ selectedUserId, userSelected }) => {
    const [searchQuery, setSearchQuery] = useState<string>();
    const [searchOpened, setSearchOpened] = useState(false);

    const { data: users } = useUsers();
    const items: IUser[] = users ?? [];

    const itemsForDropdown = items.map((x: IUser) => ({ header: x.displayName, key: x.id }));

    // The dropdown has to know the selected item, otherwise it clears the search query when it is closed
    const selectedItem = find(itemsForDropdown, (x: any) => x.key === selectedUserId) ?? null;

    useEffect(() => {
        const foundItem = find(items, (x: IUser) => x.id === selectedUserId);
        if (foundItem) {
            setSearchQuery(foundItem.displayName);
            setSearchOpened(false);
        }
    }, [selectedUserId, items]);

    const searchChanged = (_: any, data: any) => {
        if (!data.searchQuery) {
            userSelected();
        }

        if (data.searchQuery !== searchQuery) {
            setSearchQuery(data.searchQuery);
        }
    };

    const itemChanged = (_: any, data: any) => {
        const id = data.value?.key?.toString();
        if (id) {
            const user = find(items, (u: IUser) => u.id === id);
            userSelected(user);
        }
    };

    const searchOpenChanged = (_: any, data: any) => {
        setSearchOpened(data.open);
    };

    const search = (_: any[], query: string) => {
        const lower = query.toLowerCase();
        const words = lower.split(" ");
        let filtered: IUser[] = filter(items, (x: IUser) => {
            const name = x.displayName?.toLowerCase() ?? "";

            // tslint:disable-next-line: no-shadowed-variable
            const match: boolean[] = words.map((_: any) => false);
            for (let index = 0; index < words.length; index++) {
                match[index] = name.indexOf(words[index]) > -1;
            }

            return match.filter((m: boolean) => m).length === match.length;
        });

        return filtered.map((x: IUser) => ({ header: x.displayName, key: x.id }));
    };

    return (
        <Dropdown
            items={itemsForDropdown}
            value={selectedItem}
            open={searchOpened}
            onOpenChange={searchOpenChanged}
            onChange={itemChanged}
            searchQuery={searchQuery}
            onSearchQueryChange={searchChanged}
            search={search}
            fluid
        />
    );
};

export default UserPicker;
